import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ContactMessage, useUpdateMessageStatus, useDeleteMessage } from "@/hooks/useContactMessages";
import { Mail, Calendar, Globe, Monitor, ExternalLink, Copy } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

interface MessageDetailDialogProps {
  message: ContactMessage | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MessageDetailDialog = ({ message, open, onOpenChange }: MessageDetailDialogProps) => {
  const updateStatus = useUpdateMessageStatus();
  const deleteMessage = useDeleteMessage();

  if (!message) return null;

  const handleReplyClick = () => {
    if (message.status !== 'replied') {
      updateStatus.mutate({ id: message.id, status: 'replied' });
    }
    toast.success('Opening email client...');
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(message.email);
    toast.success('Email address copied to clipboard');
  };

  const subject = `Re: ${message.subject}`;
  const body = `Hi ${message.name},\n\n\n\n---\nOriginal message:\n${message.message}`;

  const handleStatusChange = (status: string) => {
    updateStatus.mutate({ id: message.id, status });
  };

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
      deleteMessage.mutate(message.id);
      onOpenChange(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-500';
      case 'read': return 'bg-gray-500';
      case 'replied': return 'bg-green-500';
      case 'archived': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl">Message Details</DialogTitle>
            <Badge className={getStatusColor(message.status)}>
              {message.status}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Contact Info */}
          <div className="space-y-3">
            <div>
              <label className="text-sm text-muted-foreground">From</label>
              <p className="font-semibold">{message.name}</p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Email</label>
              <p className="font-mono text-sm">{message.email}</p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Subject</label>
              <p className="font-semibold">{message.subject}</p>
            </div>
          </div>

          <Separator />

          {/* Message */}
          <div>
            <label className="text-sm text-muted-foreground">Message</label>
            <div className="mt-2 p-4 bg-muted rounded-lg">
              <p className="whitespace-pre-wrap">{message.message}</p>
            </div>
          </div>

          <Separator />

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>Received: {format(new Date(message.created_at), 'PPpp')}</span>
            </div>
            {message.replied_at && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-4 w-4" />
                <span>Replied: {format(new Date(message.replied_at), 'PPpp')}</span>
              </div>
            )}
            {message.ip_address && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Globe className="h-4 w-4" />
                <span className="font-mono text-xs">{message.ip_address}</span>
              </div>
            )}
            {message.user_agent && (
              <div className="flex items-center gap-2 text-muted-foreground col-span-2">
                <Monitor className="h-4 w-4" />
                <span className="text-xs truncate">{message.user_agent}</span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <div className="flex flex-1 gap-2">
            {message.status === 'new' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleStatusChange('read')}
              >
                Mark as Read
              </Button>
            )}
            {message.status !== 'archived' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleStatusChange('archived')}
              >
                Archive
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyEmail}
              className="gap-2"
            >
              <Copy className="h-4 w-4" />
              Copy Email
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
            >
              Delete
            </Button>
            <Button asChild className="gap-2">
              <a
                href={`mailto:${message.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}
                onClick={handleReplyClick}
              >
                <Mail className="h-4 w-4" />
                Reply via Email
                <ExternalLink className="h-3 w-3" />
              </a>
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MessageDetailDialog;
